import { useEffect, useMemo, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Check, User, Users } from 'lucide-react'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { RelationshipAccessLevel } from '@super/shared-types'

const ALL_GROUPS_ID = '__all-groups__'

const GROUP_TYPE_OPTIONS: {
  value: RelationshipAccessLevel
  label: string
  sublabel: string
}[] = [
  {
    value: RelationshipAccessLevel.OWNER,
    label: 'Owner',
    sublabel: 'Property owners',
  },
  {
    value: RelationshipAccessLevel.TENANT,
    label: 'Tenant',
    sublabel: 'Current tenants/renters',
  },
  {
    value: RelationshipAccessLevel.MANAGER,
    label: 'Manager',
    sublabel: 'Property managers',
  },
  {
    value: RelationshipAccessLevel.VENDOR,
    label: 'Vendor',
    sublabel: 'Vendors and contractors',
  },
]

const KNOWN_GROUP_VALUES: ReadonlySet<string> = new Set(
  Object.values(RelationshipAccessLevel),
)

function isKnownGroupValue(v: string): v is RelationshipAccessLevel {
  return KNOWN_GROUP_VALUES.has(v)
}

interface GroupTypeFilterFieldProps {
  name: string
  label?: string
  allLabel?: string
  allSublabel?: string
}

export function GroupTypeFilterField({
  name,
  label = 'Groups',
  allLabel = 'All groups',
  allSublabel = 'No caller-group restriction',
}: GroupTypeFilterFieldProps) {
  const { control } = useFormContext()
  const { field, fieldState } = useController({ name, control })
  const value: RelationshipAccessLevel[] = useMemo(() => {
    if (!Array.isArray(field.value)) return []
    return field.value.filter(
      (v): v is RelationshipAccessLevel =>
        typeof v === 'string' && isKnownGroupValue(v),
    )
  }, [field.value])

  const [showAllBadge, setShowAllBadge] = useState(true)
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current) return
    if (value.length > 0) {
      setShowAllBadge(false)
      hydratedRef.current = true
    }
  }, [value.length])

  const allItem = useMemo<SearchDropdownItem<RelationshipAccessLevel | null>>(
    () => ({
      id: ALL_GROUPS_ID,
      label: allLabel,
      sublabel: allSublabel,
      icon: <Users className="h-4 w-4" />,
      data: null,
      isExclusive: true,
    }),
    [allLabel, allSublabel],
  )

  const items = useMemo<SearchDropdownItem<RelationshipAccessLevel | null>[]>(
    () => [
      allItem,
      ...GROUP_TYPE_OPTIONS.map(opt => ({
        id: opt.value,
        label: opt.label,
        sublabel: opt.sublabel,
        icon: <User className="h-4 w-4" />,
        data: opt.value,
      })),
    ],
    [allItem],
  )

  const selectedItems = useMemo(() => {
    if (value.length > 0) {
      const valueSet = new Set<string>(value)
      return items.filter(item => valueSet.has(item.id))
    }
    return showAllBadge ? [allItem] : []
  }, [allItem, items, showAllBadge, value])

  const handleSelect = (
    selection:
      | SearchDropdownItem<RelationshipAccessLevel | null>
      | SearchDropdownItem<RelationshipAccessLevel | null>[]
      | null,
  ) => {
    const next = Array.isArray(selection)
      ? selection
      : selection
        ? [selection]
        : []
    if (next.some(i => i.id === ALL_GROUPS_ID)) {
      setShowAllBadge(true)
      field.onChange([])
      return
    }
    if (next.length === 0) {
      setShowAllBadge(false)
      field.onChange([])
      return
    }
    setShowAllBadge(false)
    const filtered = next
      .map(i => i.id)
      .filter((id): id is RelationshipAccessLevel => isKnownGroupValue(id))
    field.onChange(filtered)
  }

  return (
    <FormItem>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <SearchDropdown<RelationshipAccessLevel | null>
          items={items}
          selectedItems={selectedItems}
          onSelect={handleSelect}
          multiSelect
          placeholder={allLabel}
          searchPlaceholder="Search groups..."
          buttonClassName="w-full"
          emptyMessage="No groups found"
          renderItem={(item, isSelected) => (
            <>
              {item.icon}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.label}</div>
                {item.sublabel && (
                  <div className="text-xs text-muted-foreground truncate mt-1">
                    {item.sublabel}
                  </div>
                )}
              </div>
              {isSelected && <Check className="h-4 w-4 shrink-0" />}
            </>
          )}
        />
      </FormControl>
      {fieldState.error?.message && (
        <FormMessage>{fieldState.error.message}</FormMessage>
      )}
    </FormItem>
  )
}
